import * as React from "react";
import { useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import { Text } from "@fluentui/react";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { ArrowLeft } from "lucide-react";
import type { Email, EmailShipment } from "@src/types/api";
import type { LoadStatus } from "@src/types/api";
import type { StatusDefinition } from "@shared/status/types";
import { shipmentUiBucketFromUnifiedDoc } from "@shared/utils/shipmentUiBucket";
import GlobalShipmentRow from "./GlobalShipmentRow";
import { useTaskpaneServices } from "@src/load-agent/app/TaskpaneServicesContext";
import { getEmailSentTimeMs } from "@taskpane/utils/emailSentTime";
import { interactionLeadLocalPart } from "@shared/utils/interactionLeadDisplay";

const MODE_OPTIONS: Array<"FTL" | "LTL"> = ["FTL", "LTL"];
const FILTER_CHECKBOX_SX = {
  mr: 0.75,
  p: 0.25,
  "& .MuiSvgIcon-root": { fontSize: "14px" },
} as const;
const FILTER_AUTOCOMPLETE_COMPONENTS = {
  popper: { sx: { width: "max-content", maxWidth: "min(96vw, 720px)" } },
  paper: { sx: { width: "max-content", maxWidth: "min(96vw, 720px)", boxSizing: "border-box" } },
} as const;

const CUSTOMER_FILTER_AUTOCOMPLETE_COMPONENTS = {
  popper: {
    placement: "bottom" as const,
    sx: { width: "max-content", maxWidth: "min(96vw, 720px)" },
  },
  paper: { sx: { width: "max-content", maxWidth: "min(96vw, 720px)", boxSizing: "border-box" } },
} as const;
const FILTER_AUTOCOMPLETE_LISTBOX = {
  sx: {
    maxHeight: 280,
    boxSizing: "border-box",
    fontSize: "13px",
    "& .MuiAutocomplete-option": {
      whiteSpace: "nowrap",
      fontSize: "13px",
      minHeight: "32px",
      padding: "4px 8px",
    },
  },
} as const;

export interface StatusLoad {
  email: Email;
  shipment: EmailShipment;
}

export interface StatusLoadListProps {
  status: LoadStatus;
  loads: StatusLoad[];
  statuses: StatusDefinition[];
  onBack: () => void;
  senderOptions: string[];
  customerOptions: string[];
  userSenderFilter: string[];
  setUserSenderFilter: (v: string[]) => void;
  customerFilter: string[];
  setCustomerFilter: (v: string[]) => void;
  modeFilter: Array<"FTL" | "LTL">;
  setModeFilter: (v: Array<"FTL" | "LTL">) => void;
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
    background: "#f5f5f5",
  },
  header: {
    display: "flex",
    flexDirection: "column" as const,
    padding: "12px 14px",
    background: "#fff",
    borderBottom: "1px solid #e8e8e8",
    gap: "0px",
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    background: "#fafafa",
    cursor: "pointer",
    color: "#444",
    flexShrink: 0,
  } as React.CSSProperties,
  headerTitle: {
    fontSize: "15px",
    fontWeight: "600" as const,
    color: "#1a1a1a",
    flex: 1,
  },
  countBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: "#f0f0f0",
    fontSize: "12px",
    fontWeight: "600" as const,
    color: "#444",
    flexShrink: 0,
  } as React.CSSProperties,
  body: {
    flex: 1,
    overflowY: "auto" as const,
    padding: "12px",
  },
  empty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "120px",
    color: "#aaa",
    fontSize: "13px",
  } as React.CSSProperties,
  filtersRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "8px",
    marginTop: "8px",
    paddingTop: "8px",
    borderTop: "1px solid #eee",
  } as React.CSSProperties,
  filterControl: {
    minWidth: "84px",
    flex: "1 1 84px",
    "& .MuiInputBase-root": {
      height: "36px",
      borderRadius: "8px",
      fontSize: "13px",
      background: "#fff",
    },
    "& .MuiInputLabel-root": { fontSize: "13px", top: "-2px" },
    "& .MuiInputLabel-shrink": { top: 0 },
    "& .MuiOutlinedInput-input": { paddingTop: "8px", paddingBottom: "8px" },
    "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap", overflow: "hidden" },
    "& .MuiAutocomplete-input": { minWidth: "0 !important" },
  },
  selectedCountBubble: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "18px",
    height: "18px",
    borderRadius: "9px",
    background: "#ececec",
    color: "#666",
    fontSize: "11px",
    fontWeight: 600,
    marginRight: "6px",
    padding: "0 4px",
    flexShrink: 0,
  } as React.CSSProperties,
};

export default function StatusLoadList({
  status,
  loads,
  statuses,
  onBack,
  senderOptions,
  customerOptions,
  userSenderFilter,
  setUserSenderFilter,
  customerFilter,
  setCustomerFilter,
  modeFilter,
  setModeFilter,
}: StatusLoadListProps) {
  const { office } = useTaskpaneServices();
  const [navigatingId, setNavigatingId] = React.useState<string | null>(null);
  const [navError, setNavError] = React.useState<string | null>(null);

  const sortedLoads = useMemo(
    () =>
      [...loads].sort((a, b) => {
        const tb = getEmailSentTimeMs(b.email) ?? 0;
        const ta = getEmailSentTimeMs(a.email) ?? 0;

        return tb - ta;
      }),
    [loads]
  );

  const handleNavigate = async (email: Email) => {
    setNavigatingId(email._id);
    setNavError(null);

    let result: { ok: boolean; error?: string };

    if (email.ewsItemId) {
      // Use the stored EWS item ID directly — no extra permissions needed
      result = await office.displayEmailByEwsId(email.ewsItemId);
    } else if (email.internetMessageId) {
      // Fall back to session cache (works if user opened this email during the current session)
      result = await office.displayEmailByInternetMessageId(email.internetMessageId);
    } else {
      result = { ok: false, error: "No email identifier available." };
    }

    setNavigatingId(null);
    if (!result.ok) {
      setNavError(result.error ?? "Could not open email.");
    }
  };

  const userFilterOptionLabel = (email: string) => interactionLeadLocalPart(email) ?? email;

  const renderFilterOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string,
    selected: boolean
  ) => (
    <li {...props}>
      <Checkbox size="small" sx={FILTER_CHECKBOX_SX} checked={selected} />
      {option}
    </li>
  );

  const renderUserFilterOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string,
    selected: boolean
  ) => (
    <li {...props}>
      <Checkbox size="small" sx={FILTER_CHECKBOX_SX} checked={selected} />
      {userFilterOptionLabel(option)}
    </li>
  );

  const renderFilterInput = (
    params: React.ComponentProps<typeof TextField>,
    label: string,
    emptyPlaceholder: string,
    selectedCount: number
  ) => (
    <TextField
      {...params}
      label={label}
      placeholder={selectedCount === 0 ? emptyPlaceholder : ""}
      InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <>
            {selectedCount > 0 && <span style={styles.selectedCountBubble}>{selectedCount}</span>}
            {params.InputProps?.startAdornment}
          </>
        ),
      }}
    />
  );

  return (
    <Box sx={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <button style={styles.backBtn} onClick={onBack} aria-label="Back to dashboard">
            <ArrowLeft size={16} />
          </button>
          <Text styles={{ root: styles.headerTitle }}>{status}</Text>
          <span style={styles.countBadge}>{loads.length}</span>
        </div>
        <div style={styles.filtersRow}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            sx={styles.filterControl}
            options={senderOptions}
            value={userSenderFilter}
            onChange={(_, newValue) => setUserSenderFilter(newValue)}
            getOptionLabel={userFilterOptionLabel}
            isOptionEqualToValue={(a, b) => a === b}
            componentsProps={FILTER_AUTOCOMPLETE_COMPONENTS}
            ListboxProps={FILTER_AUTOCOMPLETE_LISTBOX}
            renderTags={() => null}
            renderOption={(props, option, { selected }) =>
              renderUserFilterOption(props, option, selected)
            }
            renderInput={(params) =>
              renderFilterInput(params, "User", "All users", userSenderFilter.length)
            }
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            sx={styles.filterControl}
            options={customerOptions}
            value={customerFilter}
            onChange={(_, newValue) => setCustomerFilter(newValue)}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(a, b) => a === b}
            componentsProps={CUSTOMER_FILTER_AUTOCOMPLETE_COMPONENTS}
            ListboxProps={FILTER_AUTOCOMPLETE_LISTBOX}
            renderTags={() => null}
            renderOption={(props, option, { selected }) =>
              renderFilterOption(props, option, selected)
            }
            renderInput={(params) =>
              renderFilterInput(params, "Customer", "All customers", customerFilter.length)
            }
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            sx={styles.filterControl}
            options={MODE_OPTIONS}
            value={modeFilter}
            onChange={(_, newValue) => setModeFilter(newValue)}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(a, b) => a === b}
            componentsProps={FILTER_AUTOCOMPLETE_COMPONENTS}
            ListboxProps={FILTER_AUTOCOMPLETE_LISTBOX}
            renderTags={() => null}
            renderOption={(props, option, { selected }) =>
              renderFilterOption(props, option, selected)
            }
            renderInput={(params) =>
              renderFilterInput(params, "Mode", "All modes", modeFilter.length)
            }
          />
        </div>
      </div>

      {navError && (
        <Box sx={{ background: "#fff3f3", borderBottom: "1px solid #fcc", padding: "8px 14px" }}>
          <Text styles={{ root: { fontSize: "12px", color: "#c0392b" } }}>{navError}</Text>
        </Box>
      )}

      <div style={styles.body}>
        {loads.length === 0 ? (
          <div style={styles.empty}>No loads in this status.</div>
        ) : (
          sortedLoads.map(({ email, shipment }) => {
            const mode = shipmentUiBucketFromUnifiedDoc(shipment);
            const key = `${email._id}-${(shipment as { shipmentId?: string }).shipmentId ?? mode}`;

            return (
              <GlobalShipmentRow
                key={key}
                email={email}
                shipment={shipment}
                statuses={statuses}
                onNavigate={() => void handleNavigate(email)}
              />
            );
          })
        )}
        {navigatingId && (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "12px" }}>
            <Spinner size={SpinnerSize.small} label="Opening email..." />
          </Box>
        )}
      </div>
    </Box>
  );
}
